import { useEffect, useMemo, useState } from 'react';
import type { Edge, Node } from '@xyflow/react';
import type { WorkflowDefinitionResponse } from '@erp/rpc';

import { definitionToGraph } from 'src/lib/workflows/definitionToGraph';

function randomNodeDelayMs(): number {
  return Math.floor(500 + Math.random() * 501);
}

function randomEdgeDelayMs(): number {
  return Math.floor(200 + Math.random() * 201);
}

export function useAnimatedWorkflow(definition: WorkflowDefinitionResponse): {
  nodes: Node[];
  edges: Edge[];
  isAnimating: boolean;
} {
  const fullGraph = useMemo(() => definitionToGraph(definition), [definition]);

  // Pre-compute node ID to array index map for efficient edge reveal lookups
  const nodeIdToIndex = useMemo(() => {
    const map = new Map<string, number>();
    fullGraph.nodes.forEach((node, idx) => {
      map.set(node.id, idx);
    });
    return map;
  }, [fullGraph.nodes]);

  const [revealedNodeIds, setRevealedNodeIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [revealedEdgeIds, setRevealedEdgeIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const timeouts: Array<ReturnType<typeof setTimeout>> = [];

    setRevealedNodeIds(new Set());
    setRevealedEdgeIds(new Set());
    setIsAnimating(true);

    if (fullGraph.nodes.length === 0) {
      setIsAnimating(false);
      return () => {
        // no-op
      };
    }

    let cumulativeDelayMs = 0;

    fullGraph.nodes.forEach((node, index) => {
      if (index > 0) {
        cumulativeDelayMs += randomNodeDelayMs();
      }

      const isLastNode = index === fullGraph.nodes.length - 1;

      const nodeTimeout = setTimeout(() => {
        setRevealedNodeIds((prevNodeIds) => {
          const nextNodeIds = new Set(prevNodeIds);
          nextNodeIds.add(node.id);
          return nextNodeIds;
        });

        const edgesToReveal = fullGraph.edges.filter((edge) => {
          const sourceIndex = nodeIdToIndex.get(edge.source);
          const targetIndex = nodeIdToIndex.get(edge.target);

          if (sourceIndex === undefined || targetIndex === undefined) {
            return false;
          }

          // Reveal edge when current node completes a connection to an earlier node:
          // - Current node is target, source was revealed earlier (incoming edge)
          // - Current node is source, target was revealed earlier (outgoing edge)
          return (
            (targetIndex === index && sourceIndex < index) ||
            (sourceIndex === index && targetIndex < index)
          );
        });

        if (edgesToReveal.length > 0) {
          const edgeTimeout = setTimeout(() => {
            setRevealedEdgeIds((prevEdgeIds) => {
              const nextEdgeIds = new Set(prevEdgeIds);
              for (const edge of edgesToReveal) {
                nextEdgeIds.add(edge.id);
              }
              return nextEdgeIds;
            });

            if (isLastNode) {
              setIsAnimating(false);
            }
          }, randomEdgeDelayMs());

          timeouts.push(edgeTimeout);
        } else if (isLastNode) {
          setIsAnimating(false);
        }
      }, cumulativeDelayMs);

      timeouts.push(nodeTimeout);
    });

    return () => {
      for (const timeout of timeouts) {
        clearTimeout(timeout);
      }
    };
  }, [fullGraph, nodeIdToIndex]);

  const nodes = useMemo(
    () => fullGraph.nodes.filter((node) => revealedNodeIds.has(node.id)),
    [fullGraph.nodes, revealedNodeIds],
  );

  const edges = useMemo(
    () =>
      fullGraph.edges.filter(
        (edge) =>
          revealedNodeIds.has(edge.source) &&
          revealedNodeIds.has(edge.target) &&
          revealedEdgeIds.has(edge.id),
      ),
    [fullGraph.edges, revealedEdgeIds, revealedNodeIds],
  );

  return { nodes, edges, isAnimating };
}
